import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import {
  Mail, CheckCircle2, AlertTriangle, Send, Camera, Monitor, Smartphone,
  Trash2, ShieldCheck, User as UserIcon,
} from "lucide-react";

function deviceIcon(ua = "") {
  const u = ua.toLowerCase();
  if (/iphone|android|mobile/.test(u)) return Smartphone;
  return Monitor;
}
function deviceName(ua = "") {
  const u = ua.toLowerCase();
  if (u.includes("iphone")) return "iPhone";
  if (u.includes("android")) return "Android";
  if (u.includes("mac")) return "macOS";
  if (u.includes("windows")) return "Windows";
  if (u.includes("linux")) return "Linux";
  if (u.includes("curl")) return "API client";
  return "Unknown";
}

export default function ProfilePage() {
  const { lang } = useI18n();
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", bio: "", timezone: "", avatar_url: "" });
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarRef = useRef(null);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/profile"), api.get("/auth/sessions")]);
    setData(p.data);
    setForm({
      name: p.data.user.name || "",
      phone: p.data.profile?.phone || "",
      bio: p.data.profile?.bio || "",
      timezone: p.data.profile?.timezone || p.data.preferences?.timezone || "UTC",
      avatar_url: p.data.profile?.avatar_url || "",
    });
    setSessions(s.data.sessions);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", form);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      await refreshUser();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resendVerify = async () => {
    setResendBusy(true);
    try {
      const { data: r } = await api.post("/auth/resend-verification");
      if (r.already_verified) toast.info(lang === "ar" ? "بريدك مُفعّل" : "Already verified");
      else toast.success(lang === "ar" ? "تم إرسال رابط جديد" : "Verification link sent");
      if (r.dev_verify_link) console.info("[DEV] verify link:", r.dev_verify_link);
    } catch (e) { toast.error("Failed"); }
    finally { setResendBusy(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(lang === "ar" ? "الحد الأقصى ٥ ميغابايت" : "Max 5 MB"); return; }
    setAvatarBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const up = data.file;
      const token = localStorage.getItem("nx_token");
      const url = `${API_BASE}/files/${up.storage_path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
      setForm((f) => ({ ...f, avatar_url: url }));
      await api.put("/profile", { avatar_url: url });
      await refreshUser();
      toast.success(lang === "ar" ? "تم رفع الصورة" : "Avatar uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setAvatarBusy(false); if (avatarRef.current) avatarRef.current.value = ""; }
  };

  const revokeSession = async (sid) => {
    try {
      await api.post(`/auth/sessions/${sid}/revoke`);
      toast.success(lang === "ar" ? "تم إنهاء الجلسة" : "Session revoked");
      load();
    } catch { toast.error("Failed"); }
  };

  const revokeAllOthers = async () => {
    try {
      const { data } = await api.post("/auth/sessions/revoke-all-others");
      toast.success(`${lang === "ar" ? "تم إنهاء" : "Revoked"} ${data.revoked} ${lang === "ar" ? "جلسة" : "sessions"}`);
      load();
    } catch { toast.error("Failed"); }
  };

  if (!data) return <AppShell><div className="p-12 text-center text-muted-foreground">Loading…</div></AppShell>;

  const u = data.user;
  const verified = u.email_verified;

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl">{lang === "ar" ? "ملفّي الشخصي" : "My Profile"}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {lang === "ar" ? "معلوماتك، حالة التحقّق، والجلسات النشطة." : "Your account details, verification status, and active sessions."}
        </p>
      </div>

      {/* Verification banner */}
      {!verified && (
        <div data-testid="verify-banner" className="nx-card p-4 mb-6 border-[#B8875D]/40 bg-[#B8875D]/5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#B8875D] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">{lang === "ar" ? "لم يتم تفعيل بريدك بعد" : "Your email isn't verified yet"}</div>
            <p className="text-sm text-muted-foreground mt-1">
              {lang === "ar" ? "ابحث في بريدك عن رسالة التحقّق، أو أعد الإرسال بالضغط أدناه." : "Check your inbox for the verification email, or resend it below."}
            </p>
          </div>
          <button data-testid="resend-verify-btn" onClick={resendVerify} disabled={resendBusy} className="nx-btn-primary text-sm inline-flex items-center gap-2 shrink-0">
            <Send className="w-3 h-3" /> {resendBusy ? "..." : (lang === "ar" ? "أعد الإرسال" : "Resend")}
          </button>
        </div>
      )}
      {verified && (
        <div data-testid="verified-badge" className="nx-card p-4 mb-6 border-emerald-500/40 bg-emerald-500/5 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
          <div className="text-sm">{lang === "ar" ? "بريدك مُفعّل ✓ حسابك مؤهّل بالكامل." : "Email verified ✓ Your account is fully activated."}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Profile card */}
        <div className="nx-card p-6 lg:col-span-2" data-testid="profile-card">
          <div className="flex items-start gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-sm bg-primary text-primary-foreground text-2xl font-semibold flex items-center justify-center overflow-hidden">
                {form.avatar_url ? <img src={form.avatar_url} alt="" className="w-full h-full object-cover" /> : ((u.name || u.email).slice(0, 2).toUpperCase())}
              </div>
              <label className="absolute -bottom-2 -end-2 w-8 h-8 bg-[#B8875D] text-white rounded-sm flex items-center justify-center cursor-pointer hover:bg-[#A5754C] transition">
                <Camera className="w-3.5 h-3.5" />
                <input ref={avatarRef} data-testid="avatar-upload-input" type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])} />
              </label>
              {avatarBusy && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs rounded-sm">…</div>}
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{lang === "ar" ? "الحساب" : "Account"}</div>
              <div className="font-display text-xl mt-1">{u.name || u.email}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Mail className="w-3 h-3" /> {u.email}</div>
              <div className="mt-2 inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border border-border rounded-sm">
                {u.role.replace("_", " ")}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block text-xs">
              <span className="text-muted-foreground uppercase tracking-widest">{lang === "ar" ? "الاسم الكامل" : "Full name"}</span>
              <input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
            </label>
            <label className="block text-xs">
              <span className="text-muted-foreground uppercase tracking-widest">{lang === "ar" ? "الجوّال" : "Phone"}</span>
              <input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+966 5X XXX XXXX" dir="ltr"
                className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
            </label>
            <label className="block text-xs">
              <span className="text-muted-foreground uppercase tracking-widest">{lang === "ar" ? "المنطقة الزمنية" : "Timezone"}</span>
              <input data-testid="profile-tz" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                placeholder="Asia/Riyadh"
                className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
            </label>
            <label className="block text-xs md:col-span-2">
              <span className="text-muted-foreground uppercase tracking-widest">{lang === "ar" ? "نبذة قصيرة" : "Short bio"}</span>
              <textarea data-testid="profile-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3}
                className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
            </label>
          </div>
          <div className="mt-5 flex justify-end">
            <button data-testid="profile-save-btn" onClick={save} disabled={busy} className="nx-btn-primary">
              {busy ? "…" : (lang === "ar" ? "حفظ التغييرات" : "Save changes")}
            </button>
          </div>
        </div>

        {/* Sessions card */}
        <div className="nx-card p-6" data-testid="sessions-card">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <div className="font-medium">{lang === "ar" ? "الجلسات النشطة" : "Active sessions"}</div>
          </div>
          <div className="space-y-3 max-h-[420px] overflow-y-auto">
            {sessions.map((s) => {
              const Icon = deviceIcon(s.user_agent);
              return (
                <div key={s.session_id} data-testid={`session-${s.session_id}`} className="p-3 border border-border/60 rounded-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <Icon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="text-sm min-w-0">
                        <div className="font-medium flex items-center gap-2">
                          {deviceName(s.user_agent)}
                          {s.is_current && <span className="text-[9px] uppercase tracking-widest text-emerald-500 border border-emerald-500/40 px-1.5 py-0.5 rounded-sm">{lang === "ar" ? "الحالية" : "current"}</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate max-w-[220px]" dir="ltr">{s.ip || "—"}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {lang === "ar" ? "آخر نشاط" : "Last seen"}: {new Date(s.last_seen_at).toLocaleString(lang === "ar" ? "ar" : "en")}
                        </div>
                      </div>
                    </div>
                    {!s.is_current && (
                      <button data-testid={`session-revoke-${s.session_id}`} onClick={() => revokeSession(s.session_id)}
                        className="text-muted-foreground hover:text-destructive shrink-0" title="Revoke">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {sessions.length === 0 && <div className="text-sm text-muted-foreground py-4">—</div>}
          </div>
          {sessions.filter((s) => !s.is_current).length > 0 && (
            <button data-testid="revoke-all-btn" onClick={revokeAllOthers} className="mt-4 w-full nx-btn-secondary text-sm">
              {lang === "ar" ? "إنهاء كل الجلسات الأخرى" : "Sign out of all other sessions"}
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
